import { alpha, Box, Chip, Tabs as MuiTabs, Stack, Tab, type TabsProps, type Theme, Tooltip } from '@mui/material';

const Tabs = ({
    options,
    value,
    setValue,
    variant = 'tabs',
    tabsVariant = 'fullWidth',
    dataTestId,
}: {
    options: {
        label: string;
        value: string;
        disabled?: boolean;
        tooltipText?: string;
        chipCount?: number;
        dataTestId?: string;
        icon?: React.ReactNode;
    }[];
    value?: string;
    setValue: (value: string) => void;
    variant?: 'tabs' | 'chips';
    tabsVariant?: TabsProps['variant'];
}) => {
    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
    };
    // Use the first option as fallback when value is undefined
    const currentValue = value ?? options.filter((option) => !option.disabled)?.[0]?.value ?? '';

    if (variant === 'tabs') {
        return (
            <MuiTabs
                value={currentValue}
                onChange={handleChange}
                variant={tabsVariant}
                TabIndicatorProps={{
                    sx: {
                        backgroundColor: 'secondary.main',
                    },
                }}
                sx={{ width: '100%' }}
                visibleScrollbar
            >
                {options.map((option) => (
                    <Tab
                        key={option.value}
                        data-testid={option.dataTestId}
                        label={
                            <Stack direction='row' alignItems='center' gap={1}>
                                {option.icon}
                                {option.label}
                                {option.chipCount ? (
                                    <Chip
                                        label={option.chipCount}
                                        size='small'
                                        color='secondary'
                                        sx={{
                                            fontSize: (t: any) => t.typography.caption.fontSize,
                                            fontWeight: 'bold',
                                        }}
                                    />
                                ) : undefined}
                            </Stack>
                        }
                        value={option.value}
                        disabled={option.disabled ?? false}
                        sx={{
                            color: 'text.secondary',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&.Mui-selected': {
                                color: 'secondary.main',
                            },
                        }}
                    />
                ))}
            </MuiTabs>
        );
    }

    return (
        <MuiTabs
            value={currentValue}
            onChange={handleChange}
            sx={{
                '& .MuiTabs-indicator': {
                    display: 'none', // Hide the default indicator
                },
                '& .MuiTabs-flexContainer': {
                    gap: 1,
                    flexWrap: 'wrap',
                },
                minHeight: 'fit-content',
            }}
        >
            {options.map((option) => (
                <Tab
                    key={option.value}
                    label={
                        option.disabled && option.tooltipText ? (
                            <Tooltip title={option.tooltipText} arrow>
                                <Box component='span'>{option.label}</Box>
                            </Tooltip>
                        ) : (
                            option.label
                        )
                    }
                    value={option.value}
                    disabled={option.disabled ?? false}
                    data-testid={option.dataTestId}
                    sx={{
                        cursor: 'pointer',
                        pointerEvents: 'auto !important',
                        borderRadius: 0.5,
                        textTransform: 'none',
                        fontWeight: 400,
                        transition: 'all 0.2s ease-in-out',
                        fontSize: (theme: Theme) => theme.typography.body2.fontSize,
                        fontStyle: 'normal',
                        height: 'fit-content',
                        minHeight: 'fit-content',
                        py: 1,
                        px: 1.5,

                        // Default state (inactive)
                        backgroundColor: (theme: Theme) => theme.palette.grey[300],
                        color: (theme: Theme) => theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: (theme: Theme) => theme.palette.grey[400],
                        },

                        // Selected state (active)
                        '&.Mui-selected': {
                            fontWeight: 400,
                            color: (theme: Theme) => theme.palette.secondary?.main,
                            background: (t: Theme) => alpha(t.palette.secondary.main, 0.1),
                            '&:hover': {
                                background: (t: Theme) => alpha(t.palette.secondary.main, 0.2),
                            },
                        },
                    }}
                />
            ))}
        </MuiTabs>
    );
};

export default Tabs;
